import {makeAutoObservable, observable} from 'mobx'
import {noop} from 'ts-essentials'
import {UUID} from 'yougile-util'
import {
  Checklist,
  cleanChecklists,
  DataObjectId,
  getChecklists,
  Hub,
  HubProperties,
  Tag
} from 'yougile-model'
import {EventBus} from '@client/event-bus'
import {minimizeChatEvent, restoreChatEvent} from '@client/events'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {createUseStore} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {SeenBy} from '@extensions/seen-by'
import {Sticker, stickerCtl} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {SystemEntities} from '@extensions/system-entities'

interface State {
  columnId?: DataObjectId
  boardId?: DataObjectId

  taskId?: DataObjectId
  pinnedStickers: DataObjectId[]
  checklists: Checklist[]
  isSubtask?: boolean

  showMessageStats: boolean
  showZeroMessagesStats: boolean
  showTimeTaskInCol: boolean
  hasSubtasks: boolean
  isCompleted: boolean

  canEdit: boolean
  canEditUserSticker: boolean
  canRename: boolean
  canComplete: boolean

  stickersDependency?: string
  rightPanelOpen: boolean
  isNew?: boolean
  showIdOnTaskCard?: boolean
}

export class TaskCardStore {
  state: State

  private hub?: Hub
  private box = Sync.getCurrentSandbox()

  private chatChangeListener?: UUID
  private newSubtasksListener?: UUID
  private removedSubtasksListener?: UUID
  private newTaskListener: number
  private isParentTaskTemplate?: boolean

  constructor(task?: Hub, isParentTaskTemplate?: boolean) {
    this.state = {
      taskId: task?.id,
      pinnedStickers: [],
      checklists: [],
      showMessageStats: false,
      showZeroMessagesStats: true,
      showTimeTaskInCol: false,
      hasSubtasks: false,
      isCompleted: false,
      canEdit: false,
      canEditUserSticker: false,
      canRename: false,
      canComplete: false,
      rightPanelOpen: false
    }
    this.isParentTaskTemplate = isParentTaskTemplate
    this.hub = task

    makeAutoObservable<this, 'hub' | 'box'>(
      this,
      {
        state: observable.shallow,
        hub: false,
        box: false
      },
      {autoBind: true}
    )

    this.syncStickers()
  }

  initialize(boardId?: DataObjectId): () => void {
    if (!this.hub) {
      return noop
    }
    this.box = Sync.get(this.hub)

    this.syncOnUpdate(boardId)
    this.updateShowMessageStats()
    this.updateShowTimeTaskInCol()

    this.chatChangeListener = ChatUtils.addChatChangeListener(
      this.rightPanelStateUpdate
    )
    EventBus.on(minimizeChatEvent, this.rightPanelStateClose)
    EventBus.on(restoreChatEvent, this.rightPanelStateUpdate)

    this.newSubtasksListener = SubtaskUtil.addNewSubtasksListener(
      this.updateIsSubtask
    )
    this.removedSubtasksListener = SubtaskUtil.addRemovedSubtasksListener(
      this.updateIsSubtask
    )

    const system = SystemEntities.get(this.box.companyId)
    this.updateIsNew(system && SeenBy.isNew(this.hub, globals.currentUserId))

    if (this.state.isNew) {
      this.newTaskListener = SyncEvents.addListener(
        this.box.companyId,
        system.seenByTagId,
        () => {
          if (this.hub) {
            this.updateIsNew(SeenBy.isNew(this.hub, globals.currentUserId))
          }
          if (!this.state.isNew) {
            SyncEvents.removeListener(this.newTaskListener)
          }
        }
      )
    }
    return () => {
      ChatUtils.removeChatChangeListener(this.chatChangeListener)
      EventBus.off(minimizeChatEvent, this.rightPanelStateClose)
      EventBus.off(restoreChatEvent, this.rightPanelStateUpdate)

      SubtaskUtil.deleteNewSubtasksListener(this.newSubtasksListener)
      SubtaskUtil.deleteRemovedSubtasksListener(this.removedSubtasksListener)

      SyncEvents.removeListener(this.newTaskListener)
    }
  }

  private updateIsNew(isNew: boolean): void {
    this.state.isNew = isNew
  }

  syncOnUpdate(boardId?: DataObjectId): void {
    if (!this.hub) {
      return
    }

    this.state.canEdit = PermissionsChecker.checkEditTaskStickers(
      this.hub,
      'not-user',
      undefined
    )
    this.state.canEditUserSticker = PermissionsChecker.checkEditUserSticker(
      this.hub
    )

    this.state.canRename =
      this.isParentTaskTemplate || PermissionsChecker.checkRename(this.hub)
    this.state.canComplete =
      PermissionsChecker.checkComplete(this.hub) &&
      (!PermissionsChecker.shouldFallbackToOldCheckComplete(this.hub) ||
        PermissionsChecker.oldCheckComplete(this.hub))

    this.state.checklists = cleanChecklists(getChecklists(this.hub))

    this.state.isCompleted = this.hub.data?.gtd?.state?.stage === 'done'
    this.state.stickersDependency =
      JSON.stringify(this.hub.stickers) + this.state.isCompleted

    this.state.columnId = this.hub.location
    this.state.boardId = boardId ?? this.box.get(this.hub.location)?.location

    this.state.hasSubtasks = SubtaskUtil.hasSubtasks(this.hub)
    this.updateIsSubtask()
  }

  syncStickers(): void {
    if (!this.hub) {
      return
    }
    const stickers = Sticker.getAllPinned(this.hub, this.state.boardId) || []
    const box = Sync.getCurrentSandbox()
    const board = box.getHub(this.state.boardId)
    const timeTrackingStickerId = stickerCtl.Timetracking.getSticker(box).id
    const isTimeTrackingOn = board?.stickers?.[timeTrackingStickerId]

    if (!stickers.some(sticker => Sticker.getType(sticker) === 'user')) {
      stickers.push(Sticker.getUserSticker({companyId: globals.getCompanyId()}))
    }

    this.state.pinnedStickers = stickers
      .filter(sticker => {
        return !(!isTimeTrackingOn && sticker.id === timeTrackingStickerId)
      })
      .map(sticker => sticker.id)
  }

  private updateIsSubtask(): void {
    if (this.hub) {
      this.state.isSubtask = SubtaskUtil.getParentTaskIds(this.hub.id).some(
        id => !this.box.isDeleted(id)
      )
    }
  }

  syncOnBoardUpdate(boardId?: DataObjectId): void {
    this.syncOnUpdate(boardId)
    this.updateShowMessageStats()
    this.updateShowTimeTaskInCol()
  }

  syncOnBoardSettings(boardId?: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    if (boardId) {
      const board = box.getHub(boardId)
      if (board) {
        this.state.showIdOnTaskCard = board?.data?.settings?.showIdOnTaskCard
      }
      return
    }

    const board = box.getHub(this.state.boardId)
    if (board) {
      this.state.showIdOnTaskCard = board?.data?.settings?.showIdOnTaskCard
    }
  }

  rightPanelStateUpdate(): void {
    const openChatHub = ChatUtils.getOpenedChatHub()
    if (this.hub) {
      this.state.rightPanelOpen =
        !!openChatHub && !!this.hub && openChatHub.id === this.hub.id
    }
  }

  private rightPanelStateClose(): void {
    this.state.rightPanelOpen = false
  }

  private updateShowMessageStats(): void {
    const board = this.box.getHub(this.state.boardId)
    this.state.showMessageStats =
      !!board?.properties?.[HubProperties.showMessageStatsInBoard]
    this.state.showZeroMessagesStats =
      !!board?.properties?.[HubProperties.showZeroMessageStatsInBoard]
  }

  private updateShowTimeTaskInCol(): void {
    const board = this.box.getHub(this.state.boardId)
    this.state.showTimeTaskInCol = !!board?.data?.settings?.showTimeTaskInColumn
  }

  taskContainerRefCallback(el: HTMLDivElement): void {
    if (!el) {
      return
    }

    const mouseOver = (): void => {
      if (globals.silentMode) {
        return
      }
      if (this.hub && !SeenBy.isSeen(this.hub, globals.currentUserId)) {
        SeenBy.markSeen(this.hub, globals.currentUserId)
      }
      this.updateIsNew(false)

      el.removeEventListener('mouseover', mouseOver)
    }
    el.addEventListener('mouseover', mouseOver)
  }

  get pinnedStickers(): Tag[] {
    const box = Sync.getCurrentSandbox()
    return this.state.pinnedStickers.map(stickerId =>
      Sticker.getById(box, stickerId)
    )
  }

  get currentTask(): Hub | undefined {
    return this.hub
  }
}

export const [TaskCardContext, useTaskCardStore] = createUseStore(TaskCardStore)
